var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
import { defer, Subject, throwError } from "rxjs";
import { delay, filter, map, retryWhen, take, tap, concatMap, } from "rxjs/operators";
import { v4 as uuidv4 } from "uuid";
import { TRANSACTION_ID_HEADER, } from "@auvious/common";
import { FileTransferImpl, } from "./model";
import log from "./log";
/**
 * Supports transferring of files from one user to another.
 */
export class FileTransferService {
    /**
     * FileTransferService Constructor.
     *
     * @param commonSource AuviousClient mandatory
     * @param rtcSource optional, if provided and registered then userEndpointId parameters are
     *  automatically filled when making requests, resulting in events that have a valid userEndpointId.
     */
    constructor(commonSource, rtcSource) {
        this.fileTransferEventsSubject = new Subject();
        commonSource.subscribe((common) => {
            var _a;
            this.apiResource = common.apiResourceFactory("rtc-api/filetransfers");
            // dispose any previous subscription
            (_a = this.fileTransferEventSubscription) === null || _a === void 0 ? void 0 : _a.dispose();
            this.fileTransferEventSubscription = common.events.setEventHandlers({
                onMessageReceived: (ev) => {
                    this.fileTransferEventsSubject.next(ev.payload);
                },
            });
        });
        rtcSource === null || rtcSource === void 0 ? void 0 : rtcSource.subscribe((rtc) => (this.rtc = rtc));
    }
    /**
     * Use this to send a file. Currently only supports sending files to another registered user.
     * All registered endpoints of the target user will receive the file.
     *
     * @param options transfer parameters
     * @returns a promise holding a FileTransfer object.
     */
    transfer(options) {
        var _a, _b, _c, _d, _e;
        return __awaiter(this, void 0, void 0, function* () {
            const transactionId = uuidv4();
            const formData = new FormData();
            formData.append("file", options.blob, options.filename);
            formData.append("applicationId", options.applicationId);
            formData.append("targetType", options.targetType);
            formData.append("targetId", options.targetId);
            if ((_a = this.rtc) === null || _a === void 0 ? void 0 : _a.endpoint()) {
                formData.append("userEndpointId", this.rtc.endpoint());
            }
            if (options.conferenceId) {
                formData.append("conferenceId", options.conferenceId);
            }
            if (options.interactionId) {
                formData.append("interactionId", options.interactionId);
            }
            const response = (yield defer(() => this.apiResource.create(formData, {
                headers: {
                    "content-type": "multipart/form-data",
                    [TRANSACTION_ID_HEADER]: transactionId,
                },
            }))
                .pipe(retryWhen((errors) => errors.pipe(
            // log error message
            tap((val) => log.warn(`retrying`, val)), 
            // restart in 5 seconds
            delay(2000), take(3), concatMap((error) => throwError(error)))))
                .toPromise());
            return FileTransferImpl.create(response.fileTransferId, (_d = (_c = (_b = this.apiResource.parentClient) === null || _b === void 0 ? void 0 : _b.auth) === null || _c === void 0 ? void 0 : _c.token) === null || _d === void 0 ? void 0 : _d.userId, (_e = this.rtc) === null || _e === void 0 ? void 0 : _e.endpoint(), options, this.signedUrlProvider());
        });
    }
    /**
     * Use this to receive any files sent to your endpoint.
     *
     * @returns an observable of FileTransfer objects
     */
    incoming() {
        return this.fileTransferEventsSubject.pipe(filter((ev) => ["FileTransferredEvent"].includes(ev.type)), map((ev) => FileTransferImpl.createFromEvent(ev, this.signedUrlProvider())));
    }
    /**
     * Returns all FileTransfer objects that match interactionId.
     * If you need to get all files sent from you, you can filter by userId.
     * If you need to get all files sent to you, you can filter by targetType=="USER" and targetId=={your-user-id}.
     *
     * @param interactionId interaction id to query by
     * @param filter if true, server filters out file transfers not sent or received by you. Default is false, which returns all file transfers related to interaction id
     * @returns a promise that resolves to an array of FileTransfer objects
     */
    findByInteractionId(interactionId, filter = false) {
        return __awaiter(this, void 0, void 0, function* () {
            return this.find({ interactionId, filter });
        });
    }
    /**
     * Returns all FileTransfer objects that match conferenceId.
     * If you need to get all files sent from you, you can filter by userId.
     * If you need to get all files sent to you, you can filter by targetType=="USER" and targetId=={your-user-id}.
     *
     * @param conferenceId conferenceId id to query by
     * @param filter if true, server filters out file transfers not sent or received by you. Default is false, which returns all file transfers related to conference id
     * @returns a promise that resolves to an array of FileTransfer objects
     */
    findByConferenceId(conferenceId, filter = false) {
        return __awaiter(this, void 0, void 0, function* () {
            return this.find({ conferenceId, filter });
        });
    }
    find(params) {
        return __awaiter(this, void 0, void 0, function* () {
            const fileTransfers = (yield defer(() => this.apiResource.get({
                params,
            }))
                .pipe(retryWhen((errors) => errors.pipe(
            // log error message
            tap((val) => log.warn(`retrying`, val)), 
            // restart in 5 seconds
            delay(2000), take(3), concatMap((error) => throwError(error)))))
                .toPromise());
            return fileTransfers.map((dto) => FileTransferImpl.createFromDTO(dto, this.signedUrlProvider()));
        });
    }
    signedUrlProvider() {
        return {
            get: (fileTransferId) => __awaiter(this, void 0, void 0, function* () {
                const signedUrlResponse = yield this.apiResource.create(null, {
                    urlPostfix: `${fileTransferId}/signedUrl`,
                });
                return {
                    url: signedUrlResponse.url,
                    expires: new Date(Date.parse(signedUrlResponse.validUntil)),
                };
            }),
        };
    }
}
